import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';

const CustomerFormModal = ({ customer, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    wifi_ssid: '',
    wifi_password: '',
    system_passwords: [],
    notes: ''
  });
  const [showWifiPassword, setShowWifiPassword] = useState(false);
  const [showSystemPasswords, setShowSystemPasswords] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        phone: customer.phone || '',
        email: customer.email || '',
        address: customer.address || '',
        wifi_ssid: customer.wifi_ssid || '',
        wifi_password: customer.wifi_password || '',
        system_passwords: customer.system_passwords || [],
        notes: customer.notes || ''
      });
    }
  }, [customer]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddSystemPassword = () => {
    setFormData({
      ...formData,
      system_passwords: [...formData.system_passwords, { label: '', username: '', password: '' }]
    });
  };

  const handleRemoveSystemPassword = (index) => {
    const newPasswords = formData.system_passwords.filter((_, i) => i !== index);
    setFormData({ ...formData, system_passwords: newPasswords });
  };

  const handleSystemPasswordChange = (index, field, value) => {
    const newPasswords = [...formData.system_passwords];
    newPasswords[index][field] = value;
    setFormData({ ...formData, system_passwords: newPasswords });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const url = customer
        ? `${backendUrl}/api/customers/${customer.id}`
        : `${backendUrl}/api/admin/customers`;
      
      const method = customer ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSuccess();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save customer');
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      setError('Failed to save customer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-black border-4 border-[var(--accent)] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-[var(--accent)]">
              {customer ? '[EDIT CUSTOMER]' : '[NEW CUSTOMER]'}
            </h2>
            <button onClick={onClose} className="text-[var(--accent)] hover:text-white">
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 border-2 border-red-500 bg-red-500/10 text-red-500">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div>
              <label className="block text-[var(--accent)] mb-2 font-bold">
                Customer Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                placeholder="Enter customer name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[var(--accent)] mb-2 font-bold">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                  placeholder="Phone number"
                />
              </div>

              <div>
                <label className="block text-[var(--accent)] mb-2 font-bold">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                  placeholder="Email address"
                />
              </div>
            </div>

            <div>
              <label className="block text-[var(--accent)] mb-2 font-bold">Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                rows="2"
                placeholder="Physical address"
              />
            </div>

            {/* WiFi Details */}
            <div className="border-t-2 border-[var(--accent)]/30 pt-4">
              <h3 className="text-xl font-bold text-[var(--accent)] mb-4">WiFi CREDENTIALS</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[var(--accent)] mb-2 font-bold">WiFi Network (SSID)</label>
                  <input
                    type="text"
                    name="wifi_ssid"
                    value={formData.wifi_ssid}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                    placeholder="Network name"
                  />
                </div>

                <div>
                  <label className="block text-[var(--accent)] mb-2 font-bold">WiFi Password</label>
                  <div className="relative">
                    <input
                      type={showWifiPassword ? "text" : "password"}
                      name="wifi_password"
                      value={formData.wifi_password}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)] pr-12"
                      placeholder="WiFi password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowWifiPassword(!showWifiPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--accent)] hover:text-white"
                    >
                      {showWifiPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* System Passwords */}
            <div className="border-t-2 border-[var(--accent)]/30 pt-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-[var(--accent)]">SYSTEM PASSWORDS</h3>
                <Button
                  type="button"
                  onClick={handleAddSystemPassword}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold"
                  size="sm"
                >
                  <Plus size={16} className="mr-1" /> [ADD]
                </Button>
              </div>

              {formData.system_passwords.length === 0 ? (
                <div className="text-[var(--accent)]/50 text-center py-4">
                  No system passwords added. Click [ADD] to add one.
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.system_passwords.map((sysPwd, index) => (
                    <div key={index} className="border-2 border-[var(--accent)]/30 p-4 relative">
                      <button
                        type="button"
                        onClick={() => handleRemoveSystemPassword(index)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-400"
                      >
                        <Trash2 size={16} />
                      </button>

                      <div className="space-y-3">
                        <input
                          type="text"
                          value={sysPwd.label}
                          onChange={(e) => handleSystemPasswordChange(index, 'label', e.target.value)}
                          className="w-full px-3 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                          placeholder="System name (e.g., Router Admin, Security System)"
                        />
                        <input
                          type="text"
                          value={sysPwd.username}
                          onChange={(e) => handleSystemPasswordChange(index, 'username', e.target.value)}
                          className="w-full px-3 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                          placeholder="Username (optional)"
                        />
                        <div className="relative">
                          <input
                            type={showSystemPasswords[index] ? "text" : "password"}
                            value={sysPwd.password}
                            onChange={(e) => handleSystemPasswordChange(index, 'password', e.target.value)}
                            className="w-full px-3 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)] pr-12"
                            placeholder="Password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSystemPasswords({ ...showSystemPasswords, [index]: !showSystemPasswords[index] })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--accent)] hover:text-white"
                          >
                            {showSystemPasswords[index] ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-[var(--accent)] mb-2 font-bold">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                rows="4"
                placeholder="Additional notes about this customer..."
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold"
              >
                {submitting ? 'SAVING...' : customer ? '[UPDATE CUSTOMER]' : '[CREATE CUSTOMER]'}
              </Button>
              <Button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold"
              >
                [CANCEL]
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomerFormModal;
